import React from 'react';
import type { DslTag, TagExecState } from './types';
import styles from './DslSummaryStrip.module.css';

const TITLE_MAX_CHARS = 60;

export type SummaryOutcomeKind =
  | 'pending'
  | 'loading'
  | 'time_series'
  | 'scalar'
  | 'text'
  | 'null'
  | 'error';

export interface SummaryRow {
  tagId: string;
  dsl: string;
  color: string;
  kind: SummaryOutcomeKind;
  detail: string;
}

const truncate = (s: string): string =>
  s.length <= TITLE_MAX_CHARS ? s : `${s.slice(0, TITLE_MAX_CHARS - 1)}…`;

const formatValue = (value: number): string => {
  if (!Number.isFinite(value)) return String(value);
  const abs = Math.abs(value);
  if (abs !== 0 && (abs < 0.0001 || abs >= 1e12)) return value.toExponential(4);
  return value.toFixed(4);
};

export const buildSummaryRows = (
  tags: DslTag[],
  execState: Record<string, TagExecState>,
): SummaryRow[] => {
  const rows: SummaryRow[] = [];
  for (const tag of tags) {
    const base = { tagId: tag.id, dsl: tag.dsl, color: tag.color };
    const state = execState[tag.id];
    if (!state) {
      rows.push({ ...base, kind: 'pending', detail: 'Not submitted' });
      continue;
    }
    if (state.status === 'loading') {
      rows.push({ ...base, kind: 'loading', detail: 'Running…' });
      continue;
    }
    if (state.status === 'error') {
      rows.push({
        ...base,
        kind: 'error',
        detail: state.error ?? 'Unknown error',
      });
      continue;
    }
    const result = state.result;
    if (!result) {
      rows.push({ ...base, kind: 'null', detail: 'null result' });
      continue;
    }
    if (result.type === 'time_series') {
      const finite = result.values.filter(
        (v) => v !== null && v !== undefined && Number.isFinite(v),
      );
      rows.push({
        ...base,
        kind: 'time_series',
        detail: `${finite.length} point${finite.length === 1 ? '' : 's'}`,
      });
      continue;
    }
    if (result.type === 'scalar') {
      rows.push({
        ...base,
        kind: 'scalar',
        detail: `scalar = ${formatValue(result.value)}`,
      });
      continue;
    }
    if (result.type === 'text') {
      rows.push({ ...base, kind: 'text', detail: `text: ${result.value}` });
      continue;
    }
    rows.push({ ...base, kind: 'null', detail: 'null result' });
  }
  return rows;
};

const KIND_LABEL: Record<SummaryOutcomeKind, string> = {
  pending: 'pending',
  loading: 'loading',
  time_series: 'time series',
  scalar: 'scalar',
  text: 'text',
  null: 'null',
  error: 'error',
};

const kindClass = (kind: SummaryOutcomeKind): string => {
  if (kind === 'error') return styles.badgeError;
  if (kind === 'loading') return styles.badgeLoading;
  if (kind === 'pending') return styles.badgePending;
  return styles.badgeOk;
};

export interface DslSummaryStripProps {
  tags: DslTag[];
  execState: Record<string, TagExecState>;
  hiddenTagIds?: ReadonlySet<string>;
  onToggleVisibility?: (tagId: string) => void;
}

const EMPTY_HIDDEN: ReadonlySet<string> = new Set();

export const DslSummaryStrip: React.FC<DslSummaryStripProps> = ({
  tags,
  execState,
  hiddenTagIds = EMPTY_HIDDEN,
  onToggleVisibility,
}) => {
  const rows = buildSummaryRows(tags, execState);

  if (rows.length === 0) {
    return (
      <section
        className={styles.container}
        data-testid="dsl-agent-summary-strip"
      >
        <p className={styles.emptyState}>
          Add one or more tags to see their outcomes here after submit.
        </p>
      </section>
    );
  }

  return (
    <section
      className={styles.container}
      data-testid="dsl-agent-summary-strip"
      aria-label="DSL tag outcomes"
    >
      <p className={styles.headerLabel}>Per-tag outcomes (click a time-series row to toggle on the chart)</p>
      <ul className={styles.rowList} role="list">
        {rows.map((row) => {
          const toggleable = row.kind === 'time_series' && typeof onToggleVisibility === 'function';
          const isHidden = hiddenTagIds.has(row.tagId);
          const classes = [styles.row];
          if (toggleable) classes.push(styles.rowClickable);
          if (isHidden) classes.push(styles.rowHidden);
          return (
            <li
              key={row.tagId}
              className={classes.join(' ')}
              role={toggleable ? 'button' : 'listitem'}
              tabIndex={toggleable ? 0 : undefined}
              aria-pressed={toggleable ? !isHidden : undefined}
              aria-label={
                toggleable
                  ? `${isHidden ? 'Show' : 'Hide'} trace ${row.dsl}`
                  : undefined
              }
              data-testid="dsl-agent-summary-row"
              data-tag-id={row.tagId}
              data-kind={row.kind}
              data-hidden={isHidden ? 'true' : 'false'}
              onClick={
                toggleable ? () => onToggleVisibility?.(row.tagId) : undefined
              }
              onKeyDown={
                toggleable
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onToggleVisibility?.(row.tagId);
                      }
                    }
                  : undefined
              }
            >
              <span
                className={styles.swatch}
                style={{ backgroundColor: row.color }}
                aria-hidden="true"
              />
              <span className={styles.dsl} title={row.dsl}>
                {truncate(row.dsl)}
              </span>
              <span className={`${styles.badge} ${kindClass(row.kind)}`}>
                {KIND_LABEL[row.kind]}
              </span>
              <span className={styles.detail} title={row.detail}>
                {row.detail}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
